import axios from "axios";
import Input from "../form/Input";
import { useForm } from "react-hook-form";
import mixpanel from "../../utils/mixpanel";
import BumpOfferModal from "./BumpOfferModal";
import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { astrolearnBaseImg } from "@/marketingUtils/astrolearnBaseImg";
import ProcessingModal from "./ProcessingModal";
import { useRouter } from "next/router";
import tracker from "@/utils/tracker";
import { RazorpayHandlerResponse } from "@/types/paymentTypes/razorpay";
import { getCookie } from "cookies-next";

type Inputs = {
  name: string;
  email: string;
  phone: string;
};
const PAYMENT_STATUS = {
  COMPLETED: "COMPLETED",
  PENDING: "PENDING",
  FAILED: "FAILED",
};

const paymentTockens = {
  FACEBOOK:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhbW91bnQiOjk5LCJpYXQiOjE3NDQ0NDAzMDR9.6yfD5szTTP3y2FkPZAOcXbRaU3JUxNiNWdkhu0mX5zk",
  GOOGLE:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhbW91bnQiOjQ5LCJpYXQiOjE3NDQ0NDAyMzV9.grKK26hq373iIppM-D6lt7RbEZOPw4wUcrW-F5Bj0Mo",
  CUSTOM:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhbW91bnQiOjE5OSwiaWF0IjoxNzQ0NDQwMzQxfQ.FDKYqhW63lwgGlNvHJ8wBQUU8nr14a2RMAHx2R-A8QI",
};

type LeadPayLoad = {
  name: string;
  phone: string;
  email: string;
  slotId: string;
  source: string;
  comment: string;
  payload?: Object;
  reffererMetadata?: Object;
};

type OrderPayLoad = {
  masterClassId: string;
  name: string;
  phone: string;
  email: string;
  paymentMethodType: string;
  userIp: string | null;
  comment: string;
  paidMc_wa_group_link?: string;
  token: string;
  origin: string;
};

export default function MarketingEnrollNowForm({
  amount,
  isOpen,
  closeModal,
  masterClassData,
  activePaidSlotInfo,
  source,
  leadComment,
  backComment,
  activeWAGroup,
  fullDomain,
  masterClassId,
  isCustom = false,
  token,
  redirectLink,
  isBumpABTest = false,
  bucket,
  abTestType,
  bumpOfferArray,
  exportToExcel = false,
  excelSheetName,
}: {
  amount?: number;
  isOpen: boolean;
  closeModal: () => void;
  masterClassData: any;
  activePaidSlotInfo?: any;
  source?: string | string[] | undefined;
  leadComment?: string | string[] | undefined;
  backComment?: string;
  activeWAGroup?: string;
  fullDomain?: string;
  masterClassId: string;
  isCustom?: boolean;
  token?: string;
  redirectLink?: string;
  isBumpABTest?: boolean;
  abTestType?: string;
  bucket?: string;
  bumpOfferArray?: any[];
  exportToExcel?: boolean;
  excelSheetName?: string;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userData, setUserData] = useState({});
  const [userIp, setUserIp] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [isBumpModalOpen, setIsBumpModalOpen] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const router = useRouter();
  const redirectURL = redirectLink
    ? redirectLink
    : `${fullDomain}/${masterClassId}/redirect/thanks-paid`;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>();

  async function exportToExcelFunc(
    data: Inputs,
    sheetName: string,
    paise_amount: number,
  ) {
    const formdata = new FormData();
    if (!data.name || !data.email || !data.phone || !exportToExcel) return;
    const price: number = paise_amount / 100;
    formdata.append("platform", sheetName);
    formdata.append("name", data.name);
    formdata.append("phone", data.phone);
    formdata.append("email", data.email);
    formdata.append("total", price.toString());
    formdata.append("source", source as string);
    formdata.append("comment", leadComment as string);
    try {
      await axios.post(
        process.env.NEXT_PUBLIC_GOOGLE_SHEET_ASTROLEARN_LEAD!,
        formdata,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
    } catch (error) {
      console.error(error);
    }
  }

  const submitForm = async (
    data: Inputs,
    leadPayload: LeadPayLoad,
    orderPayLoad: OrderPayLoad,
  ) => {
    /* Send Lead Data */
    await sendLead(leadPayload);

    /* Open Payment Window */
    await createPaymentOrder(orderPayLoad);
  };

  const sendLead = async (payload: LeadPayLoad) => {
    /* Gtag Tracking */
    // @ts-ignore
    gtag("event", "payment_razorpay_init", {
      event_category: "payment",
      event_label: "payment_submit",
      value: masterClassId,
    });

    /*  Mixpanel Tracking */
    const mixpanelData = {
      source: source,
      paymentStage: "razorpay_init",
      payment_type: "masterClassPayment",
    };
    mixpanel.alias(payload?.phone);
    mixpanel.people.set({
      $name: payload?.name,
      $email: payload?.email,
      $phone: payload?.phone,
    });
    mixpanel.identify(payload?.phone);
    mixpanel.track("payment_initiate", mixpanelData);

    /* Set User Data */
    setUserData({
      name: payload.name,
      phone: payload.phone,
      email: payload.email,
    });

    /* Set data in localstorage */
    if (Object.keys(userData).length != 0) {
      localStorage.setItem("tfu-user-auth", JSON.stringify(userData));
    }
    await axios.post(`${process.env.NEXT_PUBLIC_BASE_API}leads`, payload);

    mixpanel.track("lead_submit", {
      source: payload.source,
      comment: payload.comment,
      platform: "astrolearn",
      type: "paid",
      masterClassId: masterClassId,
    });
  };

  const createPaymentOrder = async (payload: OrderPayLoad) => {
    setIsLoading(true);
    const masterClassName =
      masterClassData?.metaData?.workshopTitle || masterClassData?.title;

    mixpanel.track("payment_initiate", {
      source: (source as string) || "direct",
      platform: "astrolearn",
      paymentStage: "razorpay_init",
      payment_type: "discounted",
      masterClassId: masterClassId,
    });

    axios
      .post(`${process.env.NEXT_PUBLIC_BASE_API}payments/create-order`, payload)
      .then(async (res) => {
        const { id, gatewayOrderId } = res.data.data;
        let { amount } = res.data.data;

        const options = {
          prefill: {
            name: payload.name,
            email: payload.email,
            contact: "+91" + String(payload.phone),
          },
          key: process.env.NEXT_PUBLIC_RAZORPAY_KEY,
          amount: Number(amount),
          currency: "INR",
          name: `Masterclass: ${masterClassName}`,
          description:
            masterClassData?.metaData?.workshopTitle || masterClassData.title,
          image: astrolearnBaseImg,
          handler: async function (response: RazorpayHandlerResponse) {
            try {
              await axios.post(
                `${process.env.NEXT_PUBLIC_BASE_API}payments/authenticate/razorpay-payment`,
                {
                  order_id: gatewayOrderId,
                  company: "ASTROJI",
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
              );
              tracker("trackSingleCustom", `Mx Payment Mrkt`);
              tracker(
                "trackSingleCustom",
                `Mx Payment Mrkt V2`,
                undefined,
                response?.razorpay_order_id,
              );
              mixpanel.track("purchase", {
                page_referrer: document.referrer,
                platform: "astrolearn",
                amount: amount,
                masterClassId: masterClassId,
              });
              if (exportToExcel && excelSheetName) {
                setIsProcessing(true);
                await exportToExcelFunc(payload, excelSheetName, amount);
              }
              setIsProcessing(false);
              setTimeout(() => {
                window.location.href =
                  redirectURL || activeWAGroup || "https://astrolearn.co/";
              }, 500);
            } catch (error) {
              router.push(`/payment-not-verified`);
            }
          },
          modal: {
            ondismiss: () => {
              mixpanel.track("rzp_dismissed", {
                page_referrer: document.referrer,
                platform: "astrolearn",
                amount: amount,
                masterClassId: masterClassId,
                activeSlot: activePaidSlotInfo?.id,
                activeWaGroup: activeWAGroup,
                slug: masterClassData?.slug,
                abtesttype: abTestType,
                bucket: bucket,
                startDateTime: activePaidSlotInfo?.startDateTime,
              });
              window.location.href = `${window.location.origin}/${masterClassId}/redirect/cancelled?source=${source}&comment=${Comment}`;
            },
          },
          order_id: gatewayOrderId,
          theme: {
            color: "#000",
          },
        };

        // @ts-ignore
        const rzp1 = new window.Razorpay(options);
        setIsLoading(false);
        rzp1.open();
        closeModal();
      });
  };

  async function exportToBumpExcel(
    data: any,
    selectedOffers: number[],
    totalPrice: number,
    source: any,
    comment: any,
  ) {
    if (!data.name || !data.email || !data.phone) return;
    const formData = new FormData();
    formData.append("platform", "bump-details-2");
    formData.append("name", data.name);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("lalKitab", selectedOffers.includes(1) ? "Yes" : "No");
    formData.append("numerology", selectedOffers.includes(2) ? "Yes" : "No");
    formData.append("bumpTotal", totalPrice.toString());
    formData.append("source", source);
    formData.append("comment", comment);
    try {
      await axios.post(
        process.env.NEXT_PUBLIC_GOOGLE_SHEET_ASTROLEARN_LEAD!,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
    } catch (error) {
      console.error(error);
    }
  }

  const handleBumpPayment = (
    data: Inputs,
    bumpToken?: any,
    selectedOffers?: any,
    totalPrice?: any,
  ) => {
    const masterClassId = masterClassData?.id;
    const masterClassName =
      masterClassData?.metaData?.workshopTitle || masterClassData?.title;
    const sendData = {
      source: source,
      paymentStage: "razorpay_init",
      payment_type: "discounted",
      masterClassId: masterClassData?.id,
      platform: "astrolearn",
    };
    setIsLoading(true);
    mixpanel.track("payment_initiate", sendData);

    const options = {
      masterClassId: masterClassData?.id || "",
      name: data.name,
      phone: data.phone,
      email: data.email,
      paymentMethodType: backComment,
      userIp: userIp,
      comment: leadComment,
      paidMc_wa_group_link: activeWAGroup,
      origin: window.location.href,
      token: bumpToken
        ? bumpToken
        : isCustom
          ? paymentTockens.CUSTOM
          : source === "facebook"
            ? paymentTockens.FACEBOOK
            : paymentTockens.GOOGLE,
    };

    axios
      .post(`${process.env.NEXT_PUBLIC_BASE_API}payments/create-order`, options)
      .then(async (res) => {
        const { id, gatewayOrderId } = res.data.data;
        let { amount } = res.data.data;

        const options = {
          prefill: {
            name: data.name,
            email: data.email,
            contact: "+91" + String(data.phone),
          },
          key: process.env.NEXT_PUBLIC_RAZORPAY_KEY,
          amount: Number(amount),
          currency: "INR",
          name: `Masterclass: ${masterClassName}`,
          description:
            masterClassData?.metaData?.workshopTitle || masterClassData.title,
          image: astrolearnBaseImg,
          handler: async function (response: RazorpayHandlerResponse) {
            try {
              await axios.post(
                `${process.env.NEXT_PUBLIC_BASE_API}payments/authenticate/razorpay-payment`,
                {
                  order_id: gatewayOrderId,
                  company: "ASTROJI",
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
              );
              mixpanel.track("purchase", {
                page_referrer: document.referrer,
                platform: "astrolearn",
                amount: amount,
                masterClassId: masterClassId,
              });
              setIsBumpModalOpen(false);
              if (exportToExcel) {
                setIsProcessing(true);
                await exportToExcelFunc(data, excelSheetName as any, amount);
              }
              if (selectedOffers && selectedOffers.length > 0) {
                setIsProcessing(true);
                await exportToBumpExcel(
                  data,
                  selectedOffers,
                  totalPrice,
                  source,
                  backComment,
                );
              }
              setIsProcessing(false);
              window.location.href =
                redirectURL || activeWAGroup || "https://www.astrolearn.co/";
            } catch (error) {
              router.push(`/payment-not-verified`);
            }
          },
          modal: {
            ondismiss: () => {
              mixpanel.track("rzp_dismissed", {
                page_referrer: document.referrer,
                platform: "astrolearn",
                amount: amount,
                masterClassId: masterClassId,
                activeSlot: activePaidSlotInfo?.id,
                activeWaGroup: activeWAGroup,
                slug: masterClassData?.slug,
                abtesttype: abTestType,
                bucket: bucket,
                startDateTime: activePaidSlotInfo?.startDateTime,
              });
              window.location.href = `${window.location.origin}/${masterClassId}/redirect/cancelled?source=${source}&comment=${Comment}`;
            },
          },
          order_id: gatewayOrderId,
          theme: {
            color: "#000",
          },
        };

        // @ts-ignore
        const rzp1 = new window.Razorpay(options);
        rzp1.open();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  async function handleFormSumbit(data: Inputs) {
    const fbclid = getCookie("_fbc") ?? router.query.fbclid ?? undefined;
    const fbpid = getCookie("_fbp") ?? undefined;
    const leadPayload: LeadPayLoad = {
      name: data.name,
      phone: data.phone,
      email: data.email,
      slotId: activePaidSlotInfo?.id,
      source: (source as string) || "direct",
      comment: (leadComment as string) || "No Comment",
      ...(bucket &&
        abTestType && {
          payload: {
            bucket,
            abTestType,
          },
        }),
      reffererMetadata: {
        ...(fbclid && { fbclid }),
        ...(fbpid && { fbpid }),
        ...(router.query.gclid && { gclid: router.query.gclid }),
      },
    };

    const orderPayLoad: OrderPayLoad = {
      masterClassId: masterClassData?.id || "",
      name: data.name,
      phone: data.phone,
      email: data.email,
      paymentMethodType: backComment as string,
      userIp: userIp,
      comment: leadComment as string,
      paidMc_wa_group_link: activeWAGroup,
      token: token
        ? token
        : isCustom
          ? paymentTockens.CUSTOM
          : paymentTockens.GOOGLE,
      origin: window.location.href,
    };

    if (isBumpABTest) {
      sendLead(leadPayload);
      closeModal();
      setIsBumpModalOpen(true);
    } else {
      submitForm(data, leadPayload, orderPayLoad);
    }
  }

  useEffect(() => {
    // get data from localstorage
    const userDetails = JSON.parse(
      String(localStorage.getItem("tfu-user-auth")),
    );
    if (userDetails) {
      reset({
        name: userDetails.name,
        email: userDetails.email,
        phone: userDetails.phone,
      });
    }

    // get ip address of the user
    axios
      .get("https://api64.ipify.org/")
      .then((res) => {
        setUserIp(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (paymentStatus == PAYMENT_STATUS.COMPLETED) {
      mixpanel.track("purchase", {
        livekit: masterClassData?.title ?? "",
        info: localStorage["tfu-user-auth"]
          ? JSON.parse(localStorage["tfu-user-auth"])
          : null,
      });
    }
  }, [paymentStatus]);

  return (
    <div>
      <ProcessingModal isOpen={isProcessing} />
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-30" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-70" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full md:items-center justify-center md:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full flex flex-col gap-5 md:border-4 md:max-w-md transform overflow-hidden md:rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {(paymentStatus == PAYMENT_STATUS.COMPLETED && (
                    <div className="flex flex-col justify-center items-center text-center h-full">
                      <span>
                        <XMarkIcon
                          className="w-8 h-8 cursor-pointer absolute top-5 right-5"
                          onClick={closeModal}
                        />
                      </span>
                      <CheckCircleIcon className="w-20 h-20 text-green-500 mb-3 mx-auto ring-2 ring-green-400/50 rounded-full" />
                      <p>
                        <b>PAYMENT SUCCESSFUL</b>
                      </p>
                      <p>
                        Thank you for your payment. Our program manager will
                        contact you with further details.
                      </p>
                    </div>
                  )) || (
                    <div>
                      <div className="flex items-center justify-between">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6"
                        >
                          Enroll Now
                        </Dialog.Title>
                        <span>
                          <XMarkIcon
                            className="w-5 h-5 cursor-pointer"
                            onClick={closeModal}
                          />
                        </span>
                      </div>
                      {!isLoading && (
                        <div>
                          <form onSubmit={handleSubmit(handleFormSumbit)}>
                            <div className="mb-3">
                              <Input
                                label="Name"
                                type="text"
                                name="name"
                                placeholder="John Doe"
                                register={register}
                                errors={errors}
                                rules={{ required: true }}
                                outerclassName="max-sm:!w-full"
                              />
                            </div>

                            <div className="mb-3">
                              <Input
                                type="email"
                                name="email"
                                label="Email"
                                placeholder="someone@somewhere.com"
                                register={register}
                                errors={errors}
                                rules={{ required: true }}
                                outerclassName="max-sm:!w-full"
                              />
                            </div>
                            <div className="mb-3">
                              <Input
                                type="number"
                                name="phone"
                                label="phone"
                                placeholder="Enter your phone number"
                                register={register}
                                errors={errors}
                                rules={{
                                  required: true,
                                  maxLength: {
                                    value: 10,
                                    message: "Invalid Phone Number",
                                  },
                                  minLength: {
                                    value: 10,
                                    message: "Invalid Phone Number",
                                  },
                                }}
                                outerclassName="max-sm:!w-full"
                              />
                            </div>
                            <div className="mt-4 bottom-3 inset-x-3">
                              <button
                                type="submit"
                                className="btn-block mt-3 btn-blue btn-lg pulse-button !tracking-wider !text-2xl"
                              >
                                Enroll Now
                              </button>
                              <p className=" text-xs p-2 text-gray-500">
                                *You&apos;re eligible for a full refund upon
                                canceling at least 14 days before the class
                                begins.
                              </p>
                            </div>
                          </form>
                        </div>
                      )}
                      {isLoading && (
                        <div role="status">
                          <svg
                            aria-hidden="true"
                            className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                              fill="currentColor"
                            />
                            <path
                              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                              fill="currentFill"
                            />
                          </svg>
                          <span className="sr-only">Loading...</span>
                        </div>
                      )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <BumpOfferModal
        isOpen={isBumpModalOpen}
        setIsOpen={setIsBumpModalOpen}
        onClose={handleBumpPayment as any}
        data={userData}
        loading={isLoading}
        token={
          amount == 49
            ? paymentTockens.GOOGLE
            : amount == 99
              ? paymentTockens.FACEBOOK
              : paymentTockens.CUSTOM
        }
        bumpOffers={bumpOfferArray}
        basePrice={amount as number}
      />
    </div>
  );
}
